/**
 * RootLayout — 로그인 후 전체 레이아웃 (헤더 + 사이드바/상단메뉴 + 탭바 + Outlet).
 *
 * 【역할】
 * - 인증된 사용자를 위한 최상위 컨테이너.
 * - 토큰 부재 시 /login 강제 이동 (탭 세션 cleanup 포함).
 * - 레이아웃 모드(side/top) 에 따라 Sider(좌) 또는 상단 Menu 렌더.
 *
 * 【주요 기능】
 * - useEffect: token 없으면 sessionStorage tab state 제거 + /login navigate.
 * - useEffect: token 있으면 loadUserInfo() — /auth/me 로 사용자 + 메뉴 트리 로드.
 * - layoutMode='side': Sider(접기 가능, expandAll/collapseAll 버튼) + Content.
 * - layoutMode='top':  상단 horizontal Menu + TabBar + Content.
 * - Spin(loading) 전역 로딩 인디케이터 (delay 400ms, framework context).
 * - ModalHost — 전역 Modal 렌더 슬롯 (usePopup 관용).
 *
 * 【보조 함수】
 * - toTopItems(nodes) — 메뉴 트리 → antd Menu items 변환.
 * - InnerRoot.onTopClick — 상단 메뉴 클릭 시 탭 오픈 + navigate.
 *
 * 【사용 위치】 router/index.tsx path='/' element.
 *
 * 【포팅 원본】 react-spring-fw RootLayout.vue + MainLayout composition.
 *
 * 【의존성】
 * - react-router-dom Outlet/useNavigate/useLocation
 * - AppHeader/AppSidebar/TabBar/ModalHost
 * - useAuthStore/useTheme/useFrameworkContext/useThemeStore/useMenuStore/useTabStore
 */
import { useEffect, useState, useMemo } from 'react'
import {
  Layout,
  Menu,
  ConfigProvider,
  Spin,
  Button,
  Flex,
  theme as antdTheme,
  type MenuProps
} from 'antd'
import { MenuFoldOutlined, MenuUnfoldOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import AppHeader from './AppHeader'
import AppSidebar from './AppSidebar'
import TabBar from './TabBar'
import ModalHost from '@/components/common/ModalHost'
import { useAuthStore } from '@/store/authStore'
import { useTheme } from '@/framework/useTheme'
import { useLabel } from '@/framework/useLabel'
import { useFrameworkContext } from '@/framework/FrameworkProvider'
import { useThemeStore } from '@/store/themeStore'
import { useMenuStore, type MenuNode } from '@/store/menuStore'
import { useTabStore } from '@/store/tabStore'
import { ensureMenusSeeded } from '@/layout/seedMenuStore'

const { Sider, Header, Content } = Layout

/**
 * RootLayout — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function RootLayout() {
  const token = useAuthStore((s) => s.token)
  const loadUserInfo = useAuthStore((s) => s.loadUserInfo)
  const navigate = useNavigate()
  const location = useLocation()
  const { themeConfig, componentDefaults } = useTheme()
  const { loading } = useFrameworkContext()

  // 인증 가드 + 사용자/메뉴 부트스트랩
  //   token 부재 ┐
  //              ├─▶ sessionStorage 탭 상태 cleanup → /login replace 이동
  //              │
  //   token 존재 ┘
  //              └─▶ loadUserInfo() (auth/me) + ensureMenusSeeded() (정적 MENU_TREE 시드)
  useEffect(() => {
    if (!token) {
      // 세션 부재 → 탭 상태 초기화 후 로그인 화면으로
      try {
        sessionStorage.removeItem('fw.tab.tabs')
        sessionStorage.removeItem('fw.tab.active')
      } catch {
        /* noop */
      }
      navigate('/login', { replace: true, state: { from: location.pathname } })
      return
    }
    void loadUserInfo()
    // 본 시스템 백엔드 /auth/me 는 menus 미반환 → 정적 MENU_TREE 시드
    ensureMenusSeeded()
  }, [token])

  if (!token) return null

  return (
    <ConfigProvider theme={themeConfig} {...componentDefaults}>
      <InnerRoot loading={loading} />
    </ConfigProvider>
  )
}

/** 메뉴 트리 → 상단 horizontal Menu items 변환 (icon 미사용 — top mode 는 라벨만). */
function toTopItems(nodes: MenuNode[]): MenuProps['items'] {
  return nodes.map((n) => ({
    key: n.path ?? n.key,
    label: n.label,
    children: n.children?.length ? toTopItems(n.children) : undefined
  }))
}

function InnerRoot({ loading }: { loading: boolean }) {
  const { token } = antdTheme.useToken()
  const { t } = useLabel() // V12-W2 fix: title={t(...)} 사용을 위한 hook 호출 (이전 정비 시 누락 → ReferenceError → 화면 크래시 + /login 튕김 원인)
  const layoutMode = useThemeStore((s) => s.layout)
  const themeMode = useThemeStore((s) => s.mode)
  const siderTheme: 'dark' | 'light' = themeMode === 'dark' ? 'dark' : 'light'
  const siderIconColor = themeMode === 'dark' ? 'rgba(255,255,255,0.65)' : token.colorTextSecondary
  const [collapsed, setCollapsed] = useState(false)
  const tree = useMenuStore((s) => s.tree)
  const expandAll = useMenuStore((s) => s.expandAll)
  const collapseAll = useMenuStore((s) => s.collapseAll)
  const openTab = useTabStore((s) => s.openTab)
  const navigate = useNavigate()
  const location = useLocation()

  const topItems = useMemo(() => toTopItems(tree), [tree])
  const onTopClick: MenuProps['onClick'] = (info) => {
    const path = info.key
    const findNode = (nodes: MenuNode[]): MenuNode | null => {
      for (const n of nodes) {
        if ((n.path ?? n.key) === path) return n
        if (n.children) {
          const r = findNode(n.children)
          if (r) return r
        }
      }
      return null
    }
    const node = findNode(tree)
    const label = node?.label ?? path
    const labelKey = node?.labelKey
    navigate(path)
    openTab({ key: path, labelKey, label, path, closable: true })
  }

  return (
    <Spin spinning={loading} delay={400} wrapperClassName="fw-root-spin">
      {layoutMode === 'top' ? (
        <Layout style={{ height: '100vh' }}>
          <Header style={{ padding: 0, background: token.colorBgContainer }}>
            <AppHeader />
          </Header>
          <Menu
            mode="horizontal"
            items={topItems}
            selectedKeys={[location.pathname]}
            onClick={onTopClick}
            style={{ paddingInline: token.padding }}
          />
          <TabBar />
          <Content
            style={{
              margin: 0,
              padding: token.padding,
              background: token.colorBgLayout,
              overflow: 'hidden',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      ) : (
        <Layout style={{ height: '100vh' }}>
          <Sider
            width={240}
            collapsedWidth={64}
            theme={siderTheme}
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{
              height: '100vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Flex
              justify="flex-end"
              align="center"
              gap={2}
              style={{ padding: token.paddingXS, height: 48, flex: '0 0 auto' }}
            >
              {!collapsed && (
                <>
                  <Button
                    type="text"
                    icon={<DownOutlined />}
                    size="small"
                    style={{ color: siderIconColor }}
                    title={t('TTL_MENU_EXPAND_ALL', '메뉴 모두 열기')}
                    onClick={expandAll}
                  />
                  <Button
                    type="text"
                    icon={<UpOutlined />}
                    size="small"
                    style={{ color: siderIconColor }}
                    title={t('TTL_MENU_COLLAPSE_ALL', '메뉴 모두 닫기')}
                    onClick={collapseAll}
                  />
                </>
              )}
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                style={{ color: siderIconColor }}
                onClick={() => setCollapsed((c) => !c)}
              />
            </Flex>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
              <AppSidebar />
            </div>
          </Sider>
          <Layout style={{ height: '100vh' }}>
            <Header style={{ padding: 0, background: token.colorBgContainer }}>
              <AppHeader />
            </Header>
            <TabBar />
            <Content
              style={{
                margin: 0,
                padding: token.padding,
                background: token.colorBgLayout,
                overflow: 'hidden',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Outlet />
            </Content>
          </Layout>
        </Layout>
      )}
      <ModalHost />
    </Spin>
  )
}
